import { motion, useReducedMotion } from 'framer-motion'
import { PenLine, Sparkles } from 'lucide-react'
import { FOUNDERS_DESK_CONTENT } from '@/lib/foundersDeskContent'
import { StoreImage } from '@/store/components/StoreImage'
import { homeEase, homeFadeUp } from './motion'

const pillars = ['Hand-poured attars', 'Heritage blends', 'Crafted with care']

export function HomeFoundersDesk() {
  const reduceMotion = useReducedMotion()
  const { name, title, message, quote, portrait } = FOUNDERS_DESK_CONTENT

  return (
    <section
      id="founders-desk"
      className="relative overflow-hidden"
      aria-labelledby="founders-desk-title"
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden>
        <div className="absolute inset-0 bg-gradient-to-b from-[#F3EFE8] via-luxury-950 to-luxury-950" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_90%_70%_at_50%_40%,rgba(212,175,55,0.08),transparent_55%)]" />
        <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#FAF8F4] to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-luxury-950" />
      </div>

      <div className="store-container relative py-14 md:py-20">
        <motion.div
          initial={reduceMotion ? false : 'hidden'}
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={homeFadeUp}
          transition={{ duration: 0.5, ease: homeEase }}
          className="mb-10 text-center md:mb-14"
        >
          <p className="type-eyebrow mb-3 inline-flex items-center justify-center gap-1.5 text-gold-400/90">
            <PenLine className="h-4 w-4 text-gold-500" aria-hidden />
            A personal note
          </p>
          <h2
            id="founders-desk-title"
            className="font-display text-2xl font-bold tracking-tight gold-gradient-text sm:text-3xl md:text-4xl lg:text-5xl"
          >
            Founder&apos;s Desk
          </h2>
          <p className="mx-auto mt-4 max-w-xl font-display text-sm leading-relaxed text-zinc-500 md:text-base">
            {FOUNDERS_DESK_CONTENT.subtitle}
          </p>
        </motion.div>

        <div className="relative mx-auto max-w-5xl">
          <motion.div
            initial={reduceMotion ? false : { opacity: 0, y: 28 }}
            whileInView={reduceMotion ? undefined : { opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.6, ease: homeEase }}
            className="flex flex-col items-center gap-10 lg:grid lg:grid-cols-[minmax(240px,300px)_minmax(0,1fr)] lg:items-center lg:gap-x-10 xl:gap-x-12"
          >
            <motion.div
              initial={reduceMotion ? false : { opacity: 0, x: -24, scale: 0.96 }}
              whileInView={reduceMotion ? undefined : { opacity: 1, x: 0, scale: 1 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.55, ease: homeEase, delay: 0.08 }}
              className="relative z-10 w-[min(72vw,260px)] shrink-0 lg:w-full lg:max-w-[300px] lg:justify-self-center"
            >
              <div className="gold-gradient-ring rounded-[1.35rem] p-[3px] shadow-2xl shadow-gold-500/25">
                <div className="overflow-hidden rounded-[1.15rem] bg-luxury-950 ring-1 ring-inset ring-white/10">
                  <div className="relative aspect-[4/5] w-full">
                    <StoreImage
                      src={portrait}
                      alt={name}
                      objectFit="cover"
                      sizing="box"
                      containerClassName="absolute inset-0"
                      className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                    />
                    <div
                      className="absolute inset-0 bg-gradient-to-t from-luxury-950/50 via-transparent to-transparent"
                      aria-hidden
                    />
                  </div>
                </div>
              </div>
              <div
                className="absolute -bottom-3 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-full border border-gold-500/30 bg-luxury-900/95 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-gold-300 shadow-lg backdrop-blur-sm"
                aria-hidden
              >
                {title}
              </div>
            </motion.div>

            <motion.article
              initial={reduceMotion ? false : { opacity: 0, x: 24 }}
              whileInView={reduceMotion ? undefined : { opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.55, ease: homeEase, delay: 0.14 }}
              className="gold-border-glow relative min-w-0 w-full overflow-hidden rounded-[1.75rem] border border-gold-500/25 bg-[#FAF8F4] shadow-2xl shadow-black/25"
            >
              <div
                className="pointer-events-none absolute inset-0 opacity-[0.35]"
                aria-hidden
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4af37' fill-opacity='0.08'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                }}
              />
              <div className="absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-transparent via-gold-500/60 to-transparent" aria-hidden />

              <div className="relative px-6 py-9 sm:px-9 sm:py-11 lg:px-10 lg:py-12 lg:pl-12">
                <div className="mb-6 flex flex-wrap items-center gap-2 lg:mb-8">
                  {pillars.map((label) => (
                    <span
                      key={label}
                      className="inline-flex items-center gap-1 rounded-full border border-gold-500/20 bg-white/70 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-gold-800/80"
                    >
                      <Sparkles className="h-3 w-3 text-gold-600" aria-hidden />
                      {label}
                    </span>
                  ))}
                </div>

                <p className="font-display text-2xl font-bold tracking-tight text-luxury-950 sm:text-3xl lg:hidden">
                  {name}
                </p>

                <div className="mb-6 hidden items-end justify-between gap-4 border-b border-gold-500/15 pb-6 lg:flex">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-gold-700/75">
                      {title}
                    </p>
                    <p className="mt-2 font-display text-3xl font-bold tracking-tight text-luxury-950">
                      {name}
                    </p>
                  </div>
                  <PenLine className="h-8 w-8 shrink-0 text-gold-500/35" aria-hidden />
                </div>

                <div className="relative store-longform space-y-4">
                  <span
                    className="pointer-events-none absolute -left-1 -top-4 font-display text-7xl leading-none text-gold-500/15 select-none sm:text-8xl"
                    aria-hidden
                  >
                    &ldquo;
                  </span>
                  {message.split(/\n\n+/).map((paragraph) => (
                    <p
                      key={paragraph.slice(0, 48)}
                      className="relative whitespace-pre-line text-zinc-700"
                    >
                      {paragraph}
                    </p>
                  ))}
                </div>

                {quote && (
                  <div className="relative mt-8 overflow-hidden rounded-2xl border border-gold-500/20 bg-gradient-to-br from-white via-gold-50/40 to-[#FAF8F4] px-5 py-6 sm:px-7 sm:py-7">
                    <div
                      className="pointer-events-none absolute -right-4 -top-6 font-display text-[5rem] leading-none text-gold-500/10"
                      aria-hidden
                    >
                      &rdquo;
                    </div>
                    <blockquote className="relative font-display text-lg italic leading-relaxed text-gold-900 sm:text-xl">
                      {quote}
                    </blockquote>
                    <p className="relative mt-4 font-display text-xs font-semibold uppercase tracking-[0.2em] text-gold-700/70">
                      — {name}
                    </p>
                  </div>
                )}
              </div>
            </motion.article>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
